import type React from "react";

/**
 * The login flow's controls, one place for every step: text left-aligned, and
 * every tap target at least 48px tall with 18px text, because many clients sign
 * in on a phone and some struggle with small links. Every action is a `Button`
 * with LOGIN_BUTTON, never an underlined text link.
 *
 * Sizes are in px: the app's root font is 13px (index.css), so a rem size here
 * renders at 13/16 of its name (h-12 is 39px, text-lg about 15px).
 */
export const LOGIN_HEADING = "text-[24px] leading-tight font-semibold text-gray-900 mb-[8px]";
export const LOGIN_TEXT = "text-[16px] text-gray-700";
export const LOGIN_LABEL = "block text-[16px] font-medium text-gray-800 mb-[8px]";
export const LOGIN_INPUT =
  "w-full h-[56px] px-[16px] text-[18px] bg-white/70 rounded-lg focus:bg-white focus:ring-2 focus:ring-white outline-hidden transition-colors";
export const LOGIN_ERROR = "p-[16px] bg-red-50 border border-red-200 rounded-lg text-[16px] text-red-700";

/** A role or a workspace to pick: a solid row at least 56px tall, easy to tap on a phone. */
export const LOGIN_PICK_ROW =
  "w-full min-h-[56px] px-[16px] py-[12px] bg-white hover:bg-gray-50 rounded-xl text-left text-[18px] font-medium text-gray-900 disabled:opacity-50 transition-colors";
export const LOGIN_PICK_HINT = "block text-[16px] font-normal text-gray-600";

/** Every login button: full width, 48px tall, 18px text. Overrides `Button`'s own size through its class merge. */
export const LOGIN_BUTTON = "w-full h-[48px] px-[24px] text-[18px]";

/** The main action looks like every other button; only its bolder text and this border set it apart. */
export const LOGIN_PRIMARY = `${LOGIN_BUTTON} border border-white`;

/**
 * A tab of a two-way choice: equal halves, label centred, as tall as a button. 16px and tight padding keep
 * "Ricevi un codice" whole in a half down to a 360px-wide phone. TabGroup concatenates, so these win by `!`.
 */
export const LOGIN_TAB = "flex-1 min-h-[48px] px-2! text-[16px]!";

/** Phones open the email keyboard, offer saved addresses, and never capitalise or correct what is typed. */
export const EMAIL_FIELD: React.InputHTMLAttributes<HTMLInputElement> = {
  type: "email",
  name: "email",
  inputMode: "email",
  autoComplete: "email",
  autoCapitalize: "none",
  autoCorrect: "off",
  spellCheck: false,
  enterKeyHint: "send",
};
